import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { API } from "@/lib/api";

const inputClass =
  "w-full rounded-none border-b border-line bg-transparent p-4 text-ink placeholder:text-stone/50 transition-colors duration-300 focus:border-bronze focus:outline-none";

export const LeadForm = ({ withMessage = false, source = "website", compact = false }) => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", email: "", locality: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error(t.form.error);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source, lang });
      toast.success(t.form.success);
      setForm({ name: "", phone: "", email: "", locality: "", message: "" });
    } catch {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className={compact ? "space-y-4" : "space-y-5"} data-testid="lead-form">
      <div>
        <p data-testid="lead-form-title" className="font-serif text-2xl md:text-3xl text-ink">{t.form.title}</p>
        <p className="mt-1 text-sm text-stone">{t.form.sub}</p>
      </div>
      <input
        data-testid="lead-form-name-input"
        required
        minLength={2}
        value={form.name}
        onChange={set("name")}
        placeholder={t.form.name}
        className={inputClass}
      />
      <input
        data-testid="lead-form-phone-input"
        required
        minLength={6}
        type="tel"
        value={form.phone}
        onChange={set("phone")}
        placeholder={t.form.phone}
        className={inputClass}
      />
      <input
        data-testid="lead-form-email-input"
        required
        type="email"
        value={form.email}
        onChange={set("email")}
        placeholder={t.form.email}
        className={inputClass}
      />
      <input
        data-testid="lead-form-locality-input"
        required
        minLength={2}
        value={form.locality}
        onChange={set("locality")}
        placeholder={t.form.locality}
        className={inputClass}
      />
      {withMessage && (
        <textarea
          data-testid="lead-form-message-input"
          value={form.message}
          onChange={set("message")}
          placeholder={t.form.message}
          rows={4}
          className={`${inputClass} resize-none`}
        />
      )}
      <button
        data-testid="lead-form-submit-button"
        type="submit"
        disabled={sending}
        className="group flex w-full items-center justify-center gap-3 bg-bronze px-8 py-4 text-sm font-medium uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-bronze-dark disabled:opacity-60"
      >
        {sending ? t.form.sending : t.form.submit}
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      <p className="text-xs text-stone/60">{t.form.note}</p>
    </form>
  );
};
